import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { getCardImageUrl } from '../assets/sets/cardImages';

// Get Firestore instance lazily to avoid initialization errors
const getDb = () => firebase.firestore();

// Card Rarity Configuration
export const CARD_RARITIES = {
  COMMON: {
    name: 'Common',
    dropRate: 0.50, // 50%
    style: {
      background: 'linear-gradient(135deg, #8e8e8e 0%, #b0b0b0 50%, #8e8e8e 100%)',
      border: '2px solid #6e6e6e',
      glow: 'none',
    }
  },
  UNCOMMON: {
    name: 'Uncommon',
    dropRate: 0.30, // 30%
    style: {
      background: 'linear-gradient(135deg, #a8d5a8 0%, #e8f5e8 25%, #ffffff 50%, #e8f5e8 75%, #a8d5a8 100%)',
      backgroundSize: '200% 200%',
      animation: 'shimmer 10s ease-in-out infinite',
      border: '2px solid #6fb86f',
      glow: '0 0 5px rgba(111, 184, 111, 0.3)',
    }
  },
  RARE: {
    name: 'Rare',
    dropRate: 0.15, // 15%
    style: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
      border: '3px solid transparent',
      borderImage: 'linear-gradient(45deg, #ff0080, #ff8c00, #40e0d0, #ff0080) 1',
      borderImageSlice: 1,
      glow: '0 0 10px rgba(255, 0, 128, 0.5)',
      holoBorder: true,
      animatedBorder: true,
    }
  },
  EPIC: {
    name: 'Epic',
    dropRate: 0.04, // 4%
    style: {
      background: 'linear-gradient(135deg, #ff0080, #ff8c00, #40e0d0, #ff0080)',
      backgroundSize: '400% 400%',
      animation: 'holoShift 3s ease infinite',
      border: '3px solid rgba(255, 255, 255, 0.8)',
      glow: '0 0 20px rgba(255, 0, 128, 0.8)',
      holoCard: true,
    }
  },
  LEGENDARY: {
    name: 'Legendary',
    dropRate: 0.01, // 1%
    style: {
      background: 'linear-gradient(135deg, #ff4500, #ff8c00, #ffd700, #ff8c00, #ff4500)',
      backgroundSize: '400% 400%',
      animation: 'holoShift 3s ease infinite',
      border: '4px solid rgba(255, 165, 0, 0.9)',
      glow: '0 0 30px rgba(255, 69, 0, 1), 0 0 60px rgba(255, 140, 0, 0.5)',
      holoCard: true,
      legendaryGlow: true,
    }
  },
};

// Card distribution for a 50-card set
const CARD_DISTRIBUTION = {
  COMMON: 25,      // 50%
  UNCOMMON: 15,    // 30%
  RARE: 7,         // 14%
  EPIC: 2,         // 4%
  LEGENDARY: 1,    // 2%
};

class CardService {
  /**
   * Initialize a card set in Firestore
   * @param {number} setNumber - The set number (e.g., 1 for "Set 1")
   * @param {string} setName - The name of the set
   * @param {string} description - Description of the set
   */
  async initializeCardSet(setNumber, setName, description) {
    const db = getDb();
    const setId = `set${setNumber}`;
    const setRef = db.collection('cardSets').doc(setId);

    const setSnapshot = await setRef.get();
    if (!setSnapshot.exists) {
      await setRef.set({
        setId,
        setNumber,
        setName,
        description,
        totalCards: 50,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    }

    return setId;
  }

  /**
   * Generate and populate a card set with 50 cards
   * @param {number} setNumber - The set number
   */
  async generateCardSet(setNumber) {
    const db = getDb();
    const setId = `set${setNumber}`;
    const batch = db.batch();

    let cardNumber = 1;

    // Generate cards for each rarity
    for (const [rarity, count] of Object.entries(CARD_DISTRIBUTION)) {
      for (let i = 0; i < count; i++) {
        const cardId = `${setId}_card${cardNumber}`;
        const cardRef = db.collection('cards').doc(cardId);

        batch.set(cardRef, {
          cardId,
          setId,
          setNumber,
          cardNumber,
          rarity,
          name: `Card #${cardNumber}`,
          description: `A ${CARD_RARITIES[rarity].name} card from Set ${setNumber}`,
          imageUrl: null, // Can be populated later
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

        cardNumber++;
      }
    }

    await batch.commit();
    console.log(`Generated 50 cards for ${setId}`);
  }

  /**
   * Get all cards in a set
   * @param {string} setId - The set ID
   * @returns {Promise<Array>}
   */
  async getCardsInSet(setId) {
    const db = getDb();
    const querySnapshot = await db.collection('cards')
      .where('setId', '==', setId)
      .get();

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      // Add image URL based on set number and card number
      const imageUrl = getCardImageUrl(data.setNumber, data.cardNumber);
      return {
        id: doc.id,
        ...data,
        imageUrl: imageUrl || data.imageUrl, // Use mapped image or fallback to stored URL
      };
    });
  }

  /**
   * Initialize user's card collection
   * @param {string} userId
   */
  async initializeUserCollection(userId) {
    const db = getDb();
    const collectionRef = db.collection('userCollections').doc(userId);
    const collectionSnapshot = await collectionRef.get();

    if (!collectionSnapshot.exists) {
      await collectionRef.set({
        userId,
        cards: {}, // cardId: { count, firstCollectedAt, lastCollectedAt }
        totalCards: 0,
        totalUniqueCards: 0,
        setsProgress: {}, // setId: { collected, total }
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    }
  }

  /**
   * Add a card to user's collection
   * @param {string} userId
   * @param {string} cardId
   */
  async addCardToCollection(userId, cardId) {
    console.log('addCardToCollection called:', { userId, cardId });
    await this.initializeUserCollection(userId);

    const db = getDb();
    const collectionRef = db.collection('userCollections').doc(userId);
    const collectionSnapshot = await collectionRef.get();
    const collectionData = collectionSnapshot.data();
    console.log('User collection data:', collectionData);

    const cards = collectionData.cards || {};
    const isNewCard = !cards[cardId];
    console.log('Is new card?', isNewCard);

    if (isNewCard) {
      cards[cardId] = {
        count: 1,
        firstCollectedAt: firebase.firestore.FieldValue.serverTimestamp(),
        lastCollectedAt: firebase.firestore.FieldValue.serverTimestamp(),
      };
    } else {
      cards[cardId].count += 1;
      cards[cardId].lastCollectedAt = firebase.firestore.FieldValue.serverTimestamp();
    }

    // Calculate totals
    const totalCards = Object.values(cards).reduce((sum, card) => sum + card.count, 0);
    const totalUniqueCards = Object.keys(cards).length;

    // Get the card details to update set progress
    const cardSnapshot = await db.collection('cards').doc(cardId).get();
    const cardData = cardSnapshot.data();

    // Update set progress
    const setsProgress = collectionData.setsProgress || {};
    if (!setsProgress[cardData.setId]) {
      setsProgress[cardData.setId] = { collected: 0, total: 50 };
    }
    if (isNewCard) {
      setsProgress[cardData.setId].collected += 1;
    }

    await collectionRef.update({
      cards,
      totalCards,
      totalUniqueCards,
      setsProgress,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    console.log('Card successfully added to collection!', {
      isNewCard,
      cardName: cardData.name,
      totalCount: cards[cardId].count,
    });

    return {
      isNewCard,
      card: cardData,
      totalCount: cards[cardId].count,
    };
  }

  /**
   * Get user's collection for a specific set
   * @param {string} userId
   * @param {string} setId
   * @returns {Promise<Object>} - Returns set cards with collection status
   */
  async getUserSetCollection(userId, setId) {
    console.log('getUserSetCollection called:', { userId, setId });
    await this.initializeUserCollection(userId);

    const db = getDb();
    const collectionSnapshot = await db.collection('userCollections').doc(userId).get();
    const collectionData = collectionSnapshot.data();
    console.log('User collection data for set view:', collectionData);

    const allCards = await this.getCardsInSet(setId);
    console.log('All cards in set:', allCards.length);
    const userCards = collectionData.cards || {};
    console.log('User collected cards:', Object.keys(userCards));

    const result = allCards.map(card => ({
      ...card,
      collected: !!userCards[card.cardId],
      count: userCards[card.cardId]?.count || 0,
    })).sort((a, b) => a.cardNumber - b.cardNumber);

    console.log('Collected cards count:', result.filter(c => c.collected).length);
    return result;
  }

  /**
   * Get a random card from a set based on rarity weights
   * @param {string} setId
   * @returns {Promise<Object>} - The selected card
   */
  async getRandomCard(setId) {
    const allCards = await this.getCardsInSet(setId);
    console.log(`getRandomCard: Found ${allCards.length} cards in ${setId}`);

    if (allCards.length === 0) {
      console.error('No cards found in set!', setId);
      return null;
    }

    // Generate random number between 0 and 1
    const rand = Math.random();
    let cumulativeProbability = 0;

    // Determine rarity based on drop rates
    let selectedRarity = 'COMMON';
    for (const [rarity, config] of Object.entries(CARD_RARITIES)) {
      cumulativeProbability += config.dropRate;
      if (rand <= cumulativeProbability) {
        selectedRarity = rarity;
        break;
      }
    }

    console.log(`Selected rarity: ${selectedRarity}`);

    // Filter cards by selected rarity
    const cardsOfRarity = allCards.filter(card => card.rarity === selectedRarity);
    console.log(`Found ${cardsOfRarity.length} cards of rarity ${selectedRarity}`);

    if (cardsOfRarity.length === 0) {
      console.error('No cards of selected rarity!', selectedRarity);
      return null;
    }

    // Pick a random card from that rarity
    const randomCard = cardsOfRarity[Math.floor(Math.random() * cardsOfRarity.length)];
    console.log('Selected random card:', randomCard);

    return randomCard;
  }

  /**
   * Get user's collection progress for all sets
   * @param {string} userId
   * @returns {Promise<Object>}
   */
  async getUserCollectionProgress(userId) {
    await this.initializeUserCollection(userId);

    const db = getDb();
    const collectionSnapshot = await db.collection('userCollections').doc(userId).get();
    return collectionSnapshot.data();
  }
}

const cardService = new CardService();
export default cardService;
